import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { orderErrorMessage } from '../../../entities/order';
import { useDiscardDraft } from '../../../entities/project';
import { Button, type ButtonProps } from '../../../shared/ui/button';
import { ConfirmDialog } from '../../../shared/ui/confirm-dialog';
import { toast } from '../../../shared/ui/sonner';

/**
 * Discards a draft behind a confirmation: the project, what was entered for it and its draft order are deleted for
 * good. Anyone with `projects.edit` in the workspace may, whoever started it; the caller decides whether to show
 * the button and the RPC refuses regardless (and refuses anything already submitted).
 */
export function DiscardDraftButton({
  projectId,
  name,
  label,
  size = 'sm',
  onDiscarded,
}: {
  projectId: string;
  /** The draft's name as shown, "Untitled" included: it is named in the confirmation. */
  name: string;
  /** The button's text; defaults to "Discard", and is always named with the draft for assistive technology. */
  label?: string;
  size?: ButtonProps['size'];
  onDiscarded?: () => void;
}) {
  const { t } = useTranslation('order');
  const discard = useDiscardDraft();
  const [confirming, setConfirming] = useState(false);
  return (
    <>
      <Button
        variant="ghost"
        size={size}
        aria-label={t('drafts.discardNamed', { name })}
        onClick={() => setConfirming(true)}
      >
        {label ?? t('drafts.discard')}
      </Button>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t('drafts.discardTitle')}
        description={t('drafts.discardBody', { name })}
        confirmLabel={t('drafts.discardYes')}
        cancelLabel={t('drafts.discardNo')}
        errorMessage={orderErrorMessage}
        onConfirm={async () => {
          await discard.mutateAsync(projectId);
          toast.success(t('drafts.discarded'));
          onDiscarded?.();
        }}
      />
    </>
  );
}
